/**
 * ReplayPlayer — plays back a ReplaySession against a TraceScene.
 *
 * Lifecycle:
 *   const player = new ReplayPlayer(traceScene, undoManager);
 *   await player.load(session);   // clears + rebuilds the initial scene
 *   player.play();                // starts the event loop
 *   player.pause();
 *   player.setPlaybackRate(2);
 *   player.stop();                // halt + detach
 *
 * Design:
 *   - Command events are pushed onto the UndoManager as they replay, so
 *     pausing and hitting Ctrl+Z undoes the last replayed step — there is
 *     no separate "replay undo" path.
 *   - Camera events are not pushed to undo (they're not user actions).
 *     Between two camera keyframes the player lerps angular values and
 *     the target position each frame.
 *   - Marker events are surfaced via the `onMarker` callback (typically
 *     the UI logs them); they do not affect scene state.
 *
 * Replay fidelity is bounded by the commands' `serialize()` fidelity,
 * which is verified in round-trip tests. The player itself does not
 * reimplement command effects.
 */

import type { TraceScene } from '../../core/TraceScene';
import type { UndoManager } from '../undo/UndoManager';
import type { UndoCommand } from '../undo/UndoCommand';
import { deserializeCommand } from '../undo/CommandCodec';
import type { CameraPose, ReplayEvent, ReplaySession } from './ReplaySession';

export interface ReplayPlayerCallbacks {
    /** Fired when a marker event is reached. */
    onMarker?: (note: string, t: number) => void;
    /** Fired when playback reaches the end of the session. */
    onComplete?: () => void;
    /** Fired if an event fails (bad deserialization, throwing redo, etc.). */
    onError?: (err: Error, event: ReplayEvent) => void;
    /** Fired on each tick — lets a UI track a scrubber cursor. */
    onTick?: (t: number) => void;
}

export class ReplayPlayer {
    private traceScene: TraceScene;
    private undoManager: UndoManager;
    private session: ReplaySession | null = null;
    private callbacks: ReplayPlayerCallbacks = {};

    private playing = false;
    /**
     * Incremented on every `play()` and `pause()`. The RAF tick captures
     * its generation at entry and bails out in its async continuation if
     * the generation has changed — prevents a stale tick from re-scheduling
     * itself after pause/seek/stop while its own `processDueEvents` is
     * still in flight.
     */
    private generation = 0;
    /** Next event index to process. */
    private cursor = 0;
    /**
     * Accumulated session time (ms) at which `cursor` sits. Advanced each
     * RAF tick by (now - lastFrame) * rate while playing.
     */
    private sessionTime = 0;
    private lastFrameWall = 0;
    private rate = 1;
    private rafHandle: number | null = null;

    /** Two-slot camera keyframe window for lerping. */
    private prevCameraEvent: { t: number; pose: CameraPose } | null = null;
    private nextCameraEvent: { t: number; pose: CameraPose } | null = null;

    constructor(traceScene: TraceScene, undoManager: UndoManager, callbacks: ReplayPlayerCallbacks = {}) {
        this.traceScene = traceScene;
        this.undoManager = undoManager;
        this.callbacks = callbacks;
    }

    get isPlaying(): boolean {
        return this.playing;
    }

    get playbackRate(): number {
        return this.rate;
    }

    /** Current session cursor time in ms (for UI readout). */
    get currentTime(): number {
        return this.sessionTime;
    }

    /**
     * Load a session: rebuild the initial scene + camera state. Clears the
     * existing scene. Must be awaited before `play()` — the replay's
     * initial state isn't in place until this resolves.
     */
    async load(session: ReplaySession): Promise<void> {
        this.stop();
        this.session = session;

        await this.resetToInitialState();

        this.cursor = 0;
        this.sessionTime = 0;
        this.prevCameraEvent = { t: 0, pose: session.initialCamera };
        this.nextCameraEvent = this.findNextCameraAfter(0) ?? null;
    }

    /** Begin or resume playback. No-op if no session is loaded. */
    play(): void {
        if (!this.session || this.playing) return;
        this.playing = true;
        this.generation++;
        this.lastFrameWall = performance.now();
        this.tick(this.generation);
    }

    /** Pause playback; resume keeps the current cursor. */
    pause(): void {
        this.playing = false;
        this.generation++;
        if (this.rafHandle !== null) {
            cancelAnimationFrame(this.rafHandle);
            this.rafHandle = null;
        }
    }

    /** Halt playback entirely (also pauses). */
    stop(): void {
        this.pause();
        this.session = null;
        this.cursor = 0;
        this.sessionTime = 0;
        this.prevCameraEvent = null;
        this.nextCameraEvent = null;
    }

    /** Set playback rate (1 = realtime, 2 = 2×, 0.5 = half speed). */
    setPlaybackRate(rate: number): void {
        this.rate = Math.max(0.01, rate);
    }

    /**
     * Advance session time to `t` ms and apply every event with `event.t <= t`
     * that hasn't been applied yet. Synchronous entry point for tests and
     * for scrub-to operations that don't want to wait for RAF ticks.
     *
     * Camera interpolation is also recomputed at the target time.
     *
     * Pauses playback for the duration of the seek to avoid racing the RAF
     * tick loop on `cursor` / `sessionTime`; resumes afterwards if it was
     * playing at entry.
     */
    async seekTo(t: number): Promise<void> {
        if (!this.session) return;
        const wasPlaying = this.playing;
        this.pause();

        if (t < this.sessionTime) {
            // Seeking backwards — reset to start and fast-forward. Simpler
            // than trying to undo replayed commands in reverse.
            await this.resetToInitialState();
            this.cursor = 0;
            this.sessionTime = 0;
            this.prevCameraEvent = { t: 0, pose: this.session.initialCamera };
            this.nextCameraEvent = this.findNextCameraAfter(0) ?? null;
        }
        this.sessionTime = t;
        await this.processDueEvents();
        this.interpolateCamera();

        if (wasPlaying) this.play();
    }

    /**
     * Apply the session's initial scene + camera. Public so a UI can "rewind
     * to start" without fully unloading the session.
     */
    async resetToInitialState(): Promise<void> {
        if (!this.session) return;

        // Clear the scene before rebuilding. Skip only the scene root
        // itself — deleting it would invalidate TraceScene. Everything else,
        // including authored top-level objects (which are parented to the
        // scene root, not null), must go so `createSceneObjectFromData`
        // can recreate them without name collisions.
        const sceneRoot = this.traceScene.getSceneRoot();
        const existing = [...this.traceScene.getSceneObjects()];
        for (const obj of existing) {
            if (obj === sceneRoot) continue;
            this.traceScene.deleteSceneObject(obj);
        }

        // Recreate each top-level object from the snapshot.
        // `createSceneObjectFromData` parents to the scene root when no
        // explicit parent is given — which is correct for top-level
        // entries captured by `getSceneDataList()`.
        const data = this.session.initialScene;
        for (const sceneData of data) {
            await this.traceScene.createSceneObjectFromData(sceneData);
        }

        this.applyCameraPose(this.session.initialCamera);
    }

    // ---------- internals ----------

    private tick = (gen: number): void => {
        // Generation guard: if `pause()` / `stop()` / `seekTo()` happened
        // between scheduling this tick and its execution, the generation
        // bumped and we must bail out.
        if (gen !== this.generation || !this.playing || !this.session) return;

        const now = performance.now();
        const deltaWall = now - this.lastFrameWall;
        this.lastFrameWall = now;
        this.sessionTime += deltaWall * this.rate;

        // Process all events whose time has arrived. Commands are awaited
        // sequentially so the next event can't start until the prior one
        // has applied — replay fidelity depends on this ordering.
        void this.processDueEvents().then(() => {
            // Re-check generation after the await: pause/seek/stop may have
            // fired while we were applying events, in which case we must
            // not re-schedule and must not mutate public state.
            if (gen !== this.generation || !this.playing || !this.session) return;
            this.interpolateCamera();
            this.callbacks.onTick?.(this.sessionTime);

            if (this.cursor >= this.session.events.length && this.sessionTime >= this.session.duration) {
                this.playing = false;
                this.generation++;
                this.callbacks.onComplete?.();
                return;
            }
            this.rafHandle = requestAnimationFrame(() => this.tick(gen));
        });
    };

    private async processDueEvents(): Promise<void> {
        if (!this.session) return;
        // No `playing` guard here: `seekTo` calls this while paused and
        // needs events to apply. The RAF tick only calls it when playing
        // anyway, so the guard was redundant there.
        while (
            this.cursor < this.session.events.length &&
            this.session.events[this.cursor].t <= this.sessionTime
        ) {
            const event = this.session.events[this.cursor];
            this.cursor++;
            await this.applyEvent(event);
        }
    }

    private async applyEvent(event: ReplayEvent): Promise<void> {
        try {
            switch (event.kind) {
                case 'command': {
                    const cmd: UndoCommand = deserializeCommand(event.payload, this.traceScene);
                    await cmd.redo();
                    // Push onto the undo stack so users can Ctrl+Z a replayed
                    // step out just like any hand-authored action. `push()`
                    // is a no-op if the UndoManager is currently undoing/
                    // redoing or frozen, which is the right default for
                    // user-initiated replay.
                    this.undoManager.push(cmd);
                    break;
                }
                case 'camera': {
                    // The event we're consuming is now "the most recent past
                    // keyframe" — it becomes `prev`. `next` is re-scanned
                    // from the remaining events so lerping forward targets
                    // the correct upcoming keyframe. Start from the cursor
                    // (not 0) so this stays O(1) amortized per event.
                    this.prevCameraEvent = { t: event.t, pose: event.pose };
                    this.nextCameraEvent = this.findNextCameraAfter(event.t, this.cursor);
                    // Apply the keyframe exactly so the next lerp has a
                    // clean origin.
                    this.applyCameraPose(event.pose);
                    break;
                }
                case 'marker': {
                    this.callbacks.onMarker?.(event.note, event.t);
                    break;
                }
            }
        } catch (err) {
            this.callbacks.onError?.(err as Error, event);
        }
    }

    private interpolateCamera(): void {
        const prev = this.prevCameraEvent;
        const next = this.nextCameraEvent;
        if (!prev || !next || next.t <= prev.t) return;
        if (this.sessionTime <= prev.t) {
            this.applyCameraPose(prev.pose);
            return;
        }
        if (this.sessionTime >= next.t) {
            this.applyCameraPose(next.pose);
            return;
        }
        const u = (this.sessionTime - prev.t) / (next.t - prev.t);
        const pose: CameraPose = {
            alpha: lerp(prev.pose.alpha, next.pose.alpha, u),
            beta: lerp(prev.pose.beta, next.pose.beta, u),
            radius: lerp(prev.pose.radius, next.pose.radius, u),
            target: [
                lerp(prev.pose.target[0], next.pose.target[0], u),
                lerp(prev.pose.target[1], next.pose.target[1], u),
                lerp(prev.pose.target[2], next.pose.target[2], u),
            ],
        };
        this.applyCameraPose(pose);
    }

    private applyCameraPose(pose: CameraPose): void {
        const cam = this.traceScene.getCamera();
        if (!cam) return;
        cam.alpha = pose.alpha;
        cam.beta = pose.beta;
        cam.radius = pose.radius;
        cam.target.set(pose.target[0], pose.target[1], pose.target[2]);
    }

    /**
     * Find the next camera keyframe strictly after time `t`. Events are
     * sorted by `t`, so we can start the scan at `this.cursor` when
     * searching forward during playback — that's where unprocessed events
     * live. For rewinds or arbitrary-time scans (e.g. after `load()`), the
     * caller passes `fromIdx = 0`.
     */
    private findNextCameraAfter(t: number, fromIdx = 0): { t: number; pose: CameraPose } | null {
        if (!this.session) return null;
        const events = this.session.events;
        for (let i = fromIdx; i < events.length; i++) {
            const e = events[i];
            if (e.kind === 'camera' && e.t > t) return { t: e.t, pose: e.pose };
        }
        return null;
    }
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}
